import { useEffect, useRef } from 'react';
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion';

interface StarfieldProps {
    /** Star travel speed toward the viewer; eases to a new value over
     *  SPEED_TRANSITION_MS instead of snapping, with no restart on change.
     *  Ignored under prefers-reduced-motion, where the field is drawn once. */
    speed?: number;
    className?: string;
    starCount?: number;
    saturation?: number;
}

interface Star {
    x: number;
    y: number;
    z: number;
    prevZ: number;
    flicker: number;
}

const HUE = 0;
const EASING = 0.2;
const MAX_FRAME_MS = 16;
const SPEED_TRANSITION_MS = 500;
const STATIC_STAR_RADIUS = 0.8;

/** Ease-in-out cubic — used to glide between speed values, not snap. */
function easeInOutCubic(t: number): number {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

function generateStars(count: number, width: number, height: number): Star[] {
    const stars: Star[] = [];
    for (let i = 0; i < count; i++) {
        stars.push({
            x: Math.random() * width,
            y: Math.random() * height,
            z: Math.random() * width,
            prevZ: 0,
            flicker: Math.random(),
        });
    }
    return stars;
}

export function Starfield({ speed = 2, className, starCount = 2000, saturation = 10 }: StarfieldProps) {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const prefersReducedMotion = usePrefersReducedMotion();
    // The target the tween below glides toward; updated here, read (and
    // eased toward) once per frame in the animation loop so a speed change
    // never restarts the star field.
    const targetSpeedRef = useRef(speed);

    useEffect(() => {
        targetSpeedRef.current = speed;
    }, [speed]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        let width = canvas.clientWidth;
        let height = canvas.clientHeight;
        canvas.width = width;
        canvas.height = height;

        let centerX = width / 2;
        let centerY = height / 2;
        let targetX = centerX;
        let targetY = centerY;
        let stars = generateStars(starCount, width, height);

        // Eased speed tween state — lives here (not in a ref/effect) since
        // only the animation loop itself needs to read or advance it.
        let currentSpeed = targetSpeedRef.current;
        let transitionFrom = currentSpeed;
        let transitionTarget = currentSpeed;
        let transitionStart = 0;

        const handleResize = () => {
            width = canvas.clientWidth;
            height = canvas.clientHeight;
            canvas.width = width;
            canvas.height = height;
            centerX = width / 2;
            centerY = height / 2;
            targetX = centerX;
            targetY = centerY;
            stars = generateStars(starCount, width, height);
        };

        const drawStaticFrame = () => {
            ctx.clearRect(0, 0, width, height);
            ctx.fillStyle = `hsl(${HUE}, ${saturation}%, 70%)`;
            for (const star of stars) {
                const k = 128.0 / star.z;
                const px = (star.x - centerX) * k + centerX;
                const py = (star.y - centerY) * k + centerY;
                if (px < 0 || px >= width || py < 0 || py >= height) continue;

                const depthFactor = Math.max(0.2, 1 - star.z / width);
                ctx.globalAlpha = Math.max(0.8, star.flicker) * depthFactor;
                ctx.beginPath();
                ctx.arc(px, py, STATIC_STAR_RADIUS, 0, Math.PI * 2);
                ctx.fill();
            }
            ctx.globalAlpha = 1.0;
        };

        if (prefersReducedMotion) {
            const handleStaticResize = () => {
                handleResize();
                drawStaticFrame();
            };
            drawStaticFrame();
            window.addEventListener('resize', handleStaticResize);
            return () => window.removeEventListener('resize', handleStaticResize);
        }

        window.addEventListener('resize', handleResize);

        let animationFrame: number;
        let lastFrameTime = 0;

        const animate = (timestamp: number) => {
            animationFrame = requestAnimationFrame(animate);
            if (timestamp - lastFrameTime < MAX_FRAME_MS) return;
            lastFrameTime = timestamp;

            if (targetSpeedRef.current !== transitionTarget) {
                transitionFrom = currentSpeed;
                transitionTarget = targetSpeedRef.current;
                transitionStart = timestamp;
            }
            const elapsed = transitionStart === 0 ? SPEED_TRANSITION_MS : timestamp - transitionStart;
            const t = Math.min(1, elapsed / SPEED_TRANSITION_MS);
            currentSpeed = transitionFrom + (transitionTarget - transitionFrom) * easeInOutCubic(t);

            ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
            ctx.fillRect(0, 0, width, height);

            centerX += (targetX - centerX) * EASING;
            centerY += (targetY - centerY) * EASING;

            for (const star of stars) {
                star.prevZ = star.z;
                star.z -= currentSpeed;

                if (star.z <= 0) {
                    star.x = Math.random() * width;
                    star.y = Math.random() * height;
                    star.z = width;
                    star.prevZ = width;
                    star.flicker = Math.random();
                }

                star.flicker += (Math.random() - 0.5) * 0.05;
                star.flicker = Math.max(0.8, Math.min(1.0, star.flicker));

                const k = 128.0 / star.z;
                const px = (star.x - centerX) * k + centerX;
                const py = (star.y - centerY) * k + centerY;

                const prevK = 128.0 / star.prevZ;
                const ppx = (star.x - centerX) * prevK + centerX;
                const ppy = (star.y - centerY) * prevK + centerY;

                if (
                    px >= 0 && px < width &&
                    py >= 0 && py < height &&
                    ppx >= 0 && ppx < width &&
                    ppy >= 0 && ppy < height
                ) {
                    const depthFactor = Math.max(0.2, 1 - star.z / width);
                    ctx.strokeStyle = `hsl(${HUE}, ${saturation}%, 70%)`;
                    ctx.globalAlpha = star.flicker * depthFactor;
                    ctx.lineWidth = 1;
                    ctx.beginPath();
                    ctx.moveTo(ppx, ppy);
                    ctx.lineTo(px, py);
                    ctx.stroke();
                    ctx.globalAlpha = 1.0;
                }
            }
        };

        animationFrame = requestAnimationFrame(animate);

        return () => {
            cancelAnimationFrame(animationFrame);
            window.removeEventListener('resize', handleResize);
        };
    }, [starCount, saturation, prefersReducedMotion]);

    return (
        <canvas
            ref={canvasRef}
            data-testid="starfield-canvas"
            data-speed={speed}
            aria-hidden="true"
            className={`w-full h-full block bg-space-950 ${className ?? ''}`}
        />
    );
}
